import { debounce } from "@utils/helper.js";
import { hasAnalyticsConsent } from "@utils/cookieConsent";

const GTM_ID = process.env.GATSBY_GTM_ID || "GTM-MGFZWDPK";

let distanceScrolled = -1;
let distanceThreshold = [
  { threshold: 0, value: "0%", sent: false },
  { threshold: 25, value: "25%", sent: false },
  { threshold: 50, value: "50%", sent: false },
  { threshold: 75, value: "75%", sent: false },
  { threshold: 90, value: "90%", sent: false },
  { threshold: 100, value: "100%", sent: false },
];

const pushDataLayer = (data) => {
  if (!hasAnalyticsConsent()) {
    return;
  }

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push(data);
};

const scrollDepth = () => {
  const scrollTop =
    document.body.scrollTop || document.documentElement.scrollTop;
  let progress = Math.round(
    (scrollTop / (document.body.scrollHeight - window.innerHeight)) * 100,
  );

  if (isNaN(progress)) progress = 0;

  if (progress > distanceScrolled) {
    distanceScrolled = progress;

    for (let i = 0; i < distanceThreshold.length; i++) {
      const status = distanceThreshold[i].sent;
      const threshold = distanceThreshold[i].threshold;
      const value = distanceThreshold[i].value;

      if (!status && progress >= threshold) {
        pushDataLayer({
          event: "gatsbyScroll",
          gatsbyScrollDistance: value,
        });
        distanceThreshold[i].sent = true;
      }
    }
  }
};

const scrollListener = debounce("scrollDepth", scrollDepth, 18);

const GTM_POLL_MAX_ATTEMPTS = 100;
let gtmPollInterval = null;

const startScrollTracking = () => {
  scrollDepth();
  // Same function reference, so repeat calls don't stack listeners
  document.addEventListener("scroll", scrollListener);
};

const GTM = {
  loadScript: () => {
    if (typeof document === "undefined") {
      return;
    }

    if (!GTM_ID) {
      return;
    }

    if (
      document.querySelector(
        `script[src*="googletagmanager.com/gtm.js?id=${GTM_ID}"]`,
      )
    ) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      platform: "gatsby",
      event: "gtm.js",
      "gtm.start": new Date().getTime(),
    });

    const script = document.createElement("script");
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_ID}`;
    document.head.appendChild(script);
  },
  init: () => {
    if (!hasAnalyticsConsent()) {
      return;
    }

    window.dataLayer = window.dataLayer || [];
    if ("google_tag_manager" in window) {
      startScrollTracking();
      return;
    }

    // Only one poll at a time (init runs on every page mount), and give up after
    // ~10s in case GTM never loads (e.g. blocked by an ad blocker)
    if (gtmPollInterval) {
      return;
    }

    let attempts = 0;
    gtmPollInterval = setInterval(() => {
      attempts += 1;
      const loaded = "google_tag_manager" in window;

      if (loaded || attempts >= GTM_POLL_MAX_ATTEMPTS) {
        clearInterval(gtmPollInterval);
        gtmPollInterval = null;
      }

      if (loaded) {
        startScrollTracking();
      }
    }, 100);
  },
  // Key events. Never pass what visitors typed (emails, names, messages):
  // sending personal data to Google Analytics breaks its terms.
  newsletterSignup: () => {
    pushDataLayer({ event: "newsletter_signup" });
  },
  commentSubmit: () => {
    pushDataLayer({ event: "comment_submit" });
  },
  contactSubmit: () => {
    pushDataLayer({ event: "contact_submit" });
  },
  toolUsed: (toolName) => {
    pushDataLayer({ event: "tool_used", toolName });
  },
  errorPage: () => {
    pushDataLayer({
      event: "error_page",
    });
  },
  videoPlay: (videoUrl) => {
    pushDataLayer({ event: "videoPlay", videoUrl });
  },
  videoProgressSeconds: (videoUrl, videoSeconds) => {
    pushDataLayer({ event: "videoProgressSeconds", videoUrl, videoSeconds });
  },
  videoProgressPercentage: (videoUrl, videoPercentage) => {
    pushDataLayer({ event: "videoProgressPercent", videoUrl, videoPercentage });
  },
  expandableContent: (event, state, expandedTitle) => {
    pushDataLayer({
      event: "expandableContent",
      expandedContentState: state,
      expandedContentTitle: expandedTitle || event.target.textContent,
    });
  },
  reset: () => {
    distanceScrolled = -1;
    distanceThreshold.forEach((threshold) => (threshold.sent = false));
  },
};

export default GTM;
export { GTM_ID };
